import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { changeLicenceExpiry } from "@/sideEffects/api";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { DatePicker } from "@/components/DatePicker";
import { LoadingSmall } from "@/components/LoadingSmall";

interface Props {
  firmLicenceGuid: string;
  expiryDate: string;
  onSuccess: () => void;
}

export const ChangeLicenceExpiryDialog = ({ firmLicenceGuid, expiryDate, onSuccess }: Props) => {
  const { t } = useTranslation();

  const formSchema = z.object({
    expiryDate: z.string().min(1),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      expiryDate,
    },
  });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (values: z.infer<typeof formSchema>) => changeLicenceExpiry(firmLicenceGuid, values.expiryDate),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getLinkedLicences"],
      });
      showSuccess(t("common.updatedSuccess"));
      onSuccess();
    },
    onError: showApiError,
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    mutation.mutate(values);
  };

  return (
    <Dialog open onOpenChange={onSuccess}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.addNewEntity", { entity: t("firm.licences") })}</DialogTitle>
          <DialogDescription>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                  control={form.control}
                  name="expiryDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("firm.expiryDate")}</FormLabel>
                      <FormControl>
                        <div>
                          <DatePicker value={field.value} onChange={field.onChange} />
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={mutation.isPending}>
                  {t("common.save")}
                  {mutation.isPending && <LoadingSmall />}
                </Button>
              </form>
            </Form>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
